import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api/client';
import { toast } from 'react-hot-toast';

export interface CVAnalysisStatus {
  status: 'pending' | 'processing' | 'fast_ai_completed' | 'completed' | 'failed';
  message: string;
  progress: number;
  candidateId?: string;
  parsed_fields?: any;
  analysis_results?: any;
  requirement_id?: string;
  processing_completed_at?: string;
  error?: string;
}

export interface UseCVAnalysisStatusOptions {
  enabled?: boolean;
  onComplete?: (status: CVAnalysisStatus) => void;
  onError?: (error: Error) => void;
  maxAttempts?: number;
  initialDelay?: number;
  pollInterval?: number;
}

export const useCVAnalysisStatus = (
  candidateId: string | null,
  requirementId?: string | null,
  options: UseCVAnalysisStatusOptions = {}
) => {
  const {
    enabled = true,
    onComplete,
    onError,
    maxAttempts = 60,
    initialDelay = 30000, // 30 seconds initial delay
    pollInterval = 10000, // 10 seconds between polls
  } = options;

  const [status, setStatus] = useState<CVAnalysisStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);

  const pollStatus = useCallback(async () => {
    if (!candidateId || !enabled || attempts >= maxAttempts) {
      if (attempts >= maxAttempts) {
        console.log('⏰ Maximum polling attempts reached');
        setIsPolling(false);
        onError?.(new Error('Maximum polling attempts reached'));
      }
      return;
    }

    // Don't poll if status is already completed
    if (status?.status === 'completed') {
      console.log('✅ CV analysis already completed, stopping polling');
      setIsPolling(false);
      return;
    }

    try {
      console.log(`🔍 Polling CV analysis status (attempt ${attempts + 1}/${maxAttempts}) for candidate: ${candidateId}`);
      
      const response = await apiClient.getCVProcessingStatus(candidateId, requirementId || undefined);
      console.log('📊 CV analysis status response:', response);

      const currentStatus: CVAnalysisStatus = {
        status: response.status as CVAnalysisStatus['status'],
        message: response.message,
        progress: (response as any).progress || 0,
        candidateId: (response as any).candidate_id,
        parsed_fields: (response as any).parsed_fields,
        analysis_results: (response as any).analysis_results,
        requirement_id: (response as any).requirement_id,
        processing_completed_at: (response as any).processing_completed_at,
        error: (response as any).error,
      };

      setStatus(currentStatus);
      setAttempts(prev => prev + 1);

      // Handle different status responses
      if (response.status === 'completed') {
        console.log('🎉 CV analysis completed! AI-enhanced data available');
        setIsPolling(false);
        onComplete?.(currentStatus);
        
        // Show success toast
        const totalTime = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
        const minutes = Math.floor(totalTime / 60);
        const seconds = totalTime % 60;
        const timeString = minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`;
        
        toast.success(`CV analysis complete! (${timeString}) Ready for auto-fill.`);
        
      } else if (response.status === 'fast_ai_completed') {
        console.log('⚡ Fast AI analysis completed, continuing for comprehensive analysis...');
        // Continue polling for comprehensive analysis
        setTimeout(pollStatus, pollInterval);
        
      } else if (response.status === 'processing' || response.status === 'analyzing') {
        console.log('🔄 CV analysis in progress, continuing to poll...');
        // Continue polling
        setTimeout(pollStatus, pollInterval);
        
      } else if (response.status === 'failed') {
        console.log('❌ CV analysis failed');
        setIsPolling(false);
        onError?.(new Error(response.message || 'CV analysis failed'));
        toast.error('CV analysis failed. Please try again.');
        
      } else {
        // pending or other status
        console.log('⏳ CV analysis pending, continuing to poll...');
        setTimeout(pollStatus, pollInterval);
      }

    } catch (error) {
      console.error('❌ Error polling CV analysis status:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts >= maxAttempts - 1) {
        setIsPolling(false);
        onError?.(error as Error);
        toast.error('Failed to check CV analysis status');
      } else {
        // Continue polling even on error
        setTimeout(pollStatus, pollInterval);
      }
    }
  }, [candidateId, enabled, attempts, maxAttempts, pollInterval, onComplete, onError, startTime]);

  const startPolling = useCallback(() => {
    if (!candidateId || isPolling) {
      console.log('🚫 Skipping CV analysis polling - already polling or no candidate ID');
      return;
    }
    
    console.log('🚀 Starting CV analysis status polling');
    setStartTime(Date.now());
    setAttempts(0);
    setIsPolling(true);
    
    // Start with initial delay
    setTimeout(pollStatus, initialDelay);
  }, [candidateId, isPolling, pollStatus, initialDelay]);

  const stopPolling = useCallback(() => {
    console.log('🛑 Stopping CV analysis status polling');
    setIsPolling(false);
  }, []);

  // Auto-start polling when candidateId is provided
  useEffect(() => {
    if (candidateId && enabled && !isPolling) {
      console.log('🔄 useCVAnalysisStatus: Starting polling for candidate:', candidateId);
      startPolling();
    }
  }, [candidateId, enabled, isPolling, startPolling]);

  // Stop polling when disabled
  useEffect(() => {
    if (!enabled && isPolling) {
      stopPolling();
    }
  }, [enabled, isPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 useCVAnalysisStatus: Cleaning up polling on unmount');
      setIsPolling(false);
    };
  }, []);

  // Cleanup when candidateId changes
  useEffect(() => {
    return () => {
      if (isPolling) {
        console.log('🧹 useCVAnalysisStatus: Cleaning up polling due to candidateId change');
        setIsPolling(false);
      }
    };
  }, [candidateId, isPolling]);

  return {
    status,
    isPolling,
    attempts,
    startPolling,
    stopPolling,
    startTime,
  };
};
